import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useUserLocationContext } from '../context/UserLocationContext';
import { useI18n } from '../i18n';
import { assetRecordLocalizedTypeLabel } from '../i18n/assetTypeLabels';
import { fetchAssetTypesForBrowse } from '../api/getAssetById';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { registerAssetThumbnailRecords } from '../utils/assetThumbnailStore';
import {
  buildHomeCarouselsFromPool,
  loadHomeCarousels,
  resetHomeAssetSearchCaches,
  type HomeCarouselBundle,
} from '../utils/homeAssetSearch';
import {
  assetRecordFirstImageSrc,
  assetRecordListAddressLine,
  assetRecordListTitle,
} from '../components/AssetGetByIdView';
import { assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import { rememberBrowseAssetListSnapshot } from '../utils/browseAssetListSnapshot';
import { assetRecordSubTitle } from '../utils/assetHomeCardSubtitle';
import {
  recordRecentlyViewedAsset,
  recentlyViewedAssetsFromCatalog,
} from '../utils/recentlyViewedAssetsStorage';
import {
  buildHomeVicinityCarousel,
  dedupeHomeCarouselByVenue,
  homeCarouselVenueDedupeKey,
} from '../utils/homeCarouselDedupe';
import { AssetImageDistanceOverlay } from '../components/AssetImageDistanceOverlay';
import { AssetImageThumb } from '../components/AssetImageThumb';
import { FavoriteAssetHeartButton } from '../favorites/FavoriteAssetHeartButton';
import { AssetTypeStripCarousel } from '../components/AssetTypeStripCarousel';
import { AssetTypeDesktopFlipCarousel } from '../components/AssetTypeDesktopFlipCarousel';
import { HomePromoAdsCarousel } from '../components/HomePromoAdsCarousel';
import { HorizontalCarousel } from '../components/HorizontalCarousel';
import carouselStyles from '../components/HorizontalCarousel.module.css';
import { PartnerLogoStrip } from '../components/PartnerLogoStrip';
import { SocialStripCarousel } from '../components/SocialStripCarousel';
import {
  resolveHomeBrowseAssetTypes,
  resolveHomeBrowseGymType,
} from '../constants/filterableAssetTypes';
import styles from './HomePage.module.css';

const HOME_CAROUSEL_LIMIT = 10;

type HomePageProps = {
  tabFocus?: number;
};

type HomeCarouselData = HomeCarouselBundle;

const EMPTY_CAROUSELS: HomeCarouselData = {
  recommended: [],
  vicinity: [],
  gyms: [],
  popular: [],
  frequentlyBooked: [],
  catalog: [],
};

function finalizeHomeCarousels(
  bundle: HomeCarouselBundle,
  coordinates: { lat: number; lng: number } | null,
): HomeCarouselData {
  const recommended = dedupeHomeCarouselByVenue(bundle.recommended).slice(0, HOME_CAROUSEL_LIMIT);
  const vicinity = buildHomeVicinityCarousel(
    recommended,
    bundle.vicinity,
    bundle.catalog,
    coordinates,
    HOME_CAROUSEL_LIMIT,
  );
  return {
    recommended,
    vicinity,
    gyms: dedupeHomeCarouselByVenue(bundle.gyms).slice(0, HOME_CAROUSEL_LIMIT),
    popular: dedupeHomeCarouselByVenue(bundle.popular).slice(0, HOME_CAROUSEL_LIMIT),
    frequentlyBooked: dedupeHomeCarouselByVenue(bundle.frequentlyBooked).slice(
      0,
      HOME_CAROUSEL_LIMIT,
    ),
    catalog: bundle.catalog,
  };
}

export function HomePage({ tabFocus = 0 }: HomePageProps) {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const { coordinates } = useUserLocationContext();
  const { openAssets } = useAppNavigation();
  const pageRef = useRef<HTMLDivElement>(null);
  const poolRef = useRef<Record<string, unknown>[] | null>(null);
  const typeCatalogRef = useRef<GeneralInfoPicklistItem[]>([]);
  const [assetTypes, setAssetTypes] = useState<GeneralInfoPicklistItem[]>([]);
  const [carousels, setCarousels] = useState<HomeCarouselData>(EMPTY_CAROUSELS);
  const [loadStatus, setLoadStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [recentViewVersion, setRecentViewVersion] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) {
      resetHomeAssetSearchCaches();
      poolRef.current = null;
      typeCatalogRef.current = [];
      setCarousels(EMPTY_CAROUSELS);
      setLoadStatus('ready');
      return;
    }

    let cancelled = false;
    const keepExisting = (poolRef.current?.length ?? 0) > 0;
    if (!keepExisting) {
      setLoadStatus('loading');
    }

    void (async () => {
      try {
        const [typeCatalog, bundle] = await Promise.all([
          fetchAssetTypesForBrowse(),
          loadHomeCarousels({
            coordinates,
            typeCatalog: [],
            limit: HOME_CAROUSEL_LIMIT * 3,
          }),
        ]);
        if (cancelled) {
          return;
        }
        typeCatalogRef.current = typeCatalog;
        setAssetTypes(typeCatalog);
        const pool = bundle.catalog;
        poolRef.current = pool;
        const ranked = buildHomeCarouselsFromPool(
          pool,
          coordinates,
          typeCatalog,
          HOME_CAROUSEL_LIMIT * 3,
        );
        const next = finalizeHomeCarousels(ranked, coordinates);
        registerAssetThumbnailRecords(
          dedupeHomeCarouselByVenue([
            ...next.recommended,
            ...next.vicinity,
            ...next.gyms,
            ...next.popular,
            ...next.frequentlyBooked,
            ...next.catalog,
          ]),
        );
        setCarousels(next);
        setLoadStatus(next.catalog.length > 0 || next.recommended.length > 0 ? 'ready' : 'error');
      } catch {
        if (cancelled) {
          return;
        }
        if (!keepExisting) {
          setCarousels(EMPTY_CAROUSELS);
          setLoadStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // Geo ready: re-rank cached pool only (no new search requests / no loading flash).
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const pool = poolRef.current;
    const typeCatalog = typeCatalogRef.current;
    if (!pool?.length || !typeCatalog.length) {
      return;
    }
    const ranked = buildHomeCarouselsFromPool(
      pool,
      coordinates,
      typeCatalog,
      HOME_CAROUSEL_LIMIT * 3,
    );
    setCarousels(finalizeHomeCarousels(ranked, coordinates));
  }, [isLoggedIn, coordinates?.lat, coordinates?.lng]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) {
      return;
    }
    if (typeof page.scrollTo === 'function') {
      page.scrollTo({ top: 0, behavior: 'auto' });
    } else {
      page.scrollTop = 0;
    }
  }, [loadStatus]);

  useEffect(() => {
    if (tabFocus < 1) {
      return;
    }
    const page = pageRef.current;
    if (!page) {
      return;
    }
    if (typeof page.scrollTo === 'function') {
      page.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      page.scrollTop = 0;
    }
  }, [tabFocus]);

  const recentlyViewedAssets = useMemo(() => {
    const recent = recentlyViewedAssetsFromCatalog(carousels.catalog, HOME_CAROUSEL_LIMIT * 3);
    return recent.slice(0, HOME_CAROUSEL_LIMIT);
  }, [carousels.catalog, recentViewVersion]);

  const browseAssetTypes = useMemo(() => resolveHomeBrowseAssetTypes(assetTypes), [assetTypes]);
  const gymBrowseType = useMemo(() => resolveHomeBrowseGymType(assetTypes), [assetTypes]);

  const onAssetOpened = (asset: Record<string, unknown>) => {
    recordRecentlyViewedAsset(asset);
    setRecentViewVersion((v) => v + 1);
  };

  const openGymsBrowse = () =>
    openAssets({
      assetTypeId: gymBrowseType.id,
      assetTypeLabel: gymBrowseType.label,
      catalogBrowse: true,
      returnView: 'home',
    });

  return (
    <div className={styles.page} ref={pageRef}>
      <SocialStripCarousel />

      <AssetTypeStripCarousel
        types={browseAssetTypes}
        onSelectType={(type) =>
          openAssets({
            assetTypeId: type.id,
            assetTypeLabel: type.label,
            catalogBrowse: true,
            returnView: 'home',
          })
        }
      />
      <AssetTypeDesktopFlipCarousel
        types={browseAssetTypes}
        onSelectType={(type) =>
          openAssets({
            assetTypeId: type.id,
            assetTypeLabel: type.label,
            catalogBrowse: true,
            returnView: 'home',
          })
        }
      />

      <AssetCarouselSection
        title={t('home.recommended')}
        assets={carousels.recommended}
        loadStatus={loadStatus}
        onShowAll={() => openAssets({ assetTypeId: null, catalogBrowse: true, returnView: 'home' })}
        onAssetOpened={onAssetOpened}
      />

      <AssetCarouselSection
        title={t('home.officesNearby')}
        assets={carousels.vicinity}
        loadStatus={loadStatus}
        onShowAll={() => openAssets({ assetTypeId: null, catalogBrowse: true, returnView: 'home' })}
        onAssetOpened={onAssetOpened}
      />

      <AssetCarouselSection
        title={t('home.gymsNearby')}
        assets={carousels.gyms}
        loadStatus={loadStatus}
        onShowAll={openGymsBrowse}
        onAssetOpened={onAssetOpened}
        emptyMessage={t('home.noGymsNearby')}
      />

      <HomePromoAdsCarousel />

      <AssetCarouselSection
        title={t('home.mostPopular')}
        assets={carousels.popular}
        loadStatus={loadStatus}
        onShowAll={() => openAssets({ assetTypeId: null, catalogBrowse: true, returnView: 'home' })}
        onAssetOpened={onAssetOpened}
      />

      <PartnerLogoStrip />

      <AssetCarouselSection
        title={t('home.frequentlyBooked')}
        assets={carousels.frequentlyBooked}
        loadStatus={loadStatus}
        onShowAll={() => openAssets({ assetTypeId: null, catalogBrowse: true, returnView: 'home' })}
        onAssetOpened={onAssetOpened}
      />

      <AssetCarouselSection
        title={t('home.recentlyViewed')}
        assets={recentlyViewedAssets}
        loadStatus={loadStatus}
        onShowAll={() => openAssets({ assetTypeId: null, catalogBrowse: true, returnView: 'home' })}
        onAssetOpened={onAssetOpened}
        emptyMessage={t('home.noRecentlyViewed')}
        isLastSection
      />
    </div>
  );
}

function assetCardKey(asset: Record<string, unknown>, index: number): string {
  const venueKey = homeCarouselVenueDedupeKey(asset);
  if (venueKey) {
    return venueKey;
  }
  const id = assetRecordPrimaryId(asset);
  if (id) {
    return id;
  }
  return `idx-${index}`;
}

function AssetCarouselSection({
  title,
  assets,
  loadStatus,
  onShowAll,
  onAssetOpened,
  emptyMessage,
  isLastSection = false,
}: {
  title: string;
  assets: Record<string, unknown>[];
  loadStatus: 'loading' | 'ready' | 'error';
  onShowAll: () => void;
  onAssetOpened: (asset: Record<string, unknown>) => void;
  emptyMessage?: string;
  isLastSection?: boolean;
}) {
  const { t, language } = useI18n();
  const isHebrew = language === 'he';
  const cards = assets.map((asset, index) => (
    <HomeAssetCard
      key={assetCardKey(asset, index)}
      asset={asset}
      compact
      onOpen={onAssetOpened}
    />
  ));

  return (
    <section
      className={[styles.section, isLastSection ? styles.sectionLast : ''].filter(Boolean).join(' ')}
    >
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <button type="button" className={styles.showAll} onClick={onShowAll}>
          {t('home.showAll')}
        </button>
      </div>
      {loadStatus === 'loading' ? (
        <p className={styles.statusLine} role="status">
          {t('home.loadingSpaces')}
        </p>
      ) : null}
      {loadStatus === 'error' ? (
        <p className={styles.statusLine} role="status">
          {t('home.loadSpacesError')}
        </p>
      ) : null}
      {assets.length === 0 && loadStatus === 'ready' ? (
        <p className={styles.statusLine}>{emptyMessage ?? t('home.noSpaces')}</p>
      ) : (
        <HorizontalCarousel
          ariaLabel={title}
          prevAriaLabel={t('home.carouselPrev', { section: title })}
          nextAriaLabel={t('home.carouselNext', { section: title })}
          contentKey={assets.map((asset, index) => assetCardKey(asset, index)).join(',')}
          scrollClassName={
            [carouselStyles.homeAssetScroll, isHebrew ? carouselStyles.homeAssetScrollRtl : '']
              .filter(Boolean)
              .join(' ')
          }
          trackItemClassName={carouselStyles.homeAssetTrackItem}
          initialScrollAlign="start"
        >
          {cards}
        </HorizontalCarousel>
      )}
    </section>
  );
}

function HomeAssetCard({
  asset,
  compact = false,
  onOpen,
}: {
  asset: Record<string, unknown>;
  compact?: boolean;
  onOpen?: (asset: Record<string, unknown>) => void;
}) {
  const { t, language } = useI18n();
  const { openAssets, showAssetInBrowse } = useAppNavigation();
  const title = assetRecordListTitle(asset);
  const typeLabel = assetRecordLocalizedTypeLabel(asset, language, t);
  const addressLine = assetRecordListAddressLine(asset, language);
  const subTitle = assetRecordSubTitle(asset);
  const imageSrc = assetRecordFirstImageSrc(asset);
  const browseLabel = [title, typeLabel, addressLine, subTitle].filter(Boolean).join(', ');

  return (
    <article
      className={[styles.assetCard, compact ? styles.assetCardCompact : ''].filter(Boolean).join(' ')}
    >
      <div className={styles.assetImageWrap}>
        <button
          type="button"
          className={styles.assetCardButton}
          onClick={() => {
            onOpen?.(asset);
            rememberBrowseAssetListSnapshot(asset);
            const id = assetRecordPrimaryId(asset);
            if (id) {
              showAssetInBrowse(id, {
                assetTypeId: null,
                returnView: 'home',
                catalogBrowse: true,
              });
            } else {
              openAssets({ assetTypeId: null, catalogBrowse: true, returnView: 'home' });
            }
          }}
          aria-label={t('home.browseAsset', { title: browseLabel })}
        >
          <AssetImageDistanceOverlay asset={asset} theme="light" compact={compact}>
            <AssetImageThumb src={imageSrc} className={styles.assetImage} />
          </AssetImageDistanceOverlay>
        </button>
        <FavoriteAssetHeartButton
          asset={asset}
          className={styles.assetHeart}
          activeClassName={styles.assetHeartActive}
        />
      </div>
      <div className={styles.assetCardText}>
        <p className={styles.assetTitle}>{title}</p>
        {typeLabel ? (
          <p className={styles.assetType} aria-label={t('list.assetType')}>
            {typeLabel}
          </p>
        ) : null}
        {addressLine ? <p className={styles.assetCity}>{addressLine}</p> : null}
        {subTitle ? <p className={styles.assetSubTitle}>{subTitle}</p> : null}
      </div>
    </article>
  );
}
